import { URL_PARSING_BASE } from 'lib/consts/hostnames'
import { REDACTED_VALUE, SECRET_PATH_PATTERNS, SECRET_QUERY_PARAMS } from 'lib/consts/urlSecrets'

interface RedactUrlSecretsParams {
  url: string
}

const SECRET_QUERY_PARAM_NAMES = new Set<string>(SECRET_QUERY_PARAMS)

const isAbsolute = (url: string) => /^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('//')

const redactPathname = (pathname: string): string =>
  SECRET_PATH_PATTERNS.reduce((path, pattern) => path.replace(pattern, `$1${REDACTED_VALUE}`), pathname)

/**
 * Keeps the input's shape (absolute, relative or bare query). Returns the input itself when nothing
 * changed, since a rebuild re-encodes the query and `hasUrlSecrets` compares against the input.
 */
export const redactUrlSecrets = ({ url }: RedactUrlSecretsParams): string => {
  if (!url.includes('?') && !hasSecretPath({ url })) {
    return url
  }

  let parsed: URL

  try {
    parsed = new URL(url, URL_PARSING_BASE)
  } catch {
    return url
  }

  const { searchParams } = parsed
  let changed = false

  Array.from(searchParams.entries()).forEach(([name, value]) => {
    const redacted = SECRET_QUERY_PARAM_NAMES.has(name) ? REDACTED_VALUE : redactUrlSecrets({ url: value })

    if (redacted !== value) {
      searchParams.set(name, redacted)
      changed = true
    }
  })

  const pathname = redactPathname(parsed.pathname)

  if (pathname !== parsed.pathname) {
    parsed.pathname = pathname
    changed = true
  }

  if (!changed) {
    return url
  }

  if (isAbsolute(url)) {
    return parsed.href
  }

  if (url.startsWith('?')) {
    return `${parsed.search}${parsed.hash}`
  }

  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

export const hasUrlSecrets = ({ url }: RedactUrlSecretsParams): boolean => redactUrlSecrets({ url }) !== url

export const hasSecretPath = ({ url }: RedactUrlSecretsParams): boolean =>
  SECRET_PATH_PATTERNS.some((pattern) => pattern.test(url))
